import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../Components/Layout';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Paper
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon,
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import BillingUsagePlans from './components/BillingUsagePlans';
import Payment from './components/Payment';
import StripeBillingHistory from './components/StripeBillingHistory';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Billing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);

  // Handle URL-based tab selection
  useEffect(() => {
    if (location.pathname === '/payment-method') {
      setTabValue(1);
    } else if (location.pathname === '/billing-usage') {
      setTabValue(0);
    } else if (location.pathname === '/billing-history') {
      setTabValue(2);
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Navigate to payment-method page when Payment Information tab is clicked
    if (newValue === 1) {
      navigate('/payment-method');
    } else if (newValue === 2) {
      navigate('/billing-history');
    } else {
      navigate('/billing-usage');
    }
  };

  return (
    <Layout>
      <Box sx={{mt: 3, px: { xs: 0, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ mb: 3, px: { xs: 2, sm: 0 } }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            color: '#111827'
          }}>
            Billing
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#6b7280',
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: 1.5
          }}>
            Manage your billing, usage, and payment information
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ width: '100%', mb: 2, mx: { xs: 0, sm: 0 } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              aria-label="billing management tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '12px', sm: '14px' },
                  minHeight: { xs: '40px', sm: '48px' },
                  px: { xs: 1, sm: 2 },
                  minWidth: { xs: 'auto', sm: 'auto' }
                },
                '& .Mui-selected': {
                  color: '#374151'
                },
                '& .MuiTabs-scrollButtons': {
                  display: { xs: 'flex', sm: 'none' }
                }
              }}
            >
              <Tab 
                icon={<AssessmentIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                label="Usage & Plans" 
                iconPosition="start"
              />
              <Tab 
                icon={<CreditCardIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                label="Payment Information" 
                iconPosition="start"
              />
              <Tab 
                icon={<AccountBalanceIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                label="Billing History" 
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <BillingUsagePlans />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Payment />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <StripeBillingHistory />
        </TabPanel>
      </Box>
    </Layout>
  );
}

export default Billing;
